import { useEffect, useState } from "react";
import { Ban } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import type { DisqualificationReason } from "@/api/leads";

// The Disqualify action requires a STRUCTURED reason code: the backend
// `DisqualificationReason` is an enum, so a free-text value can never bind
// (the previous prompt-driven flow silently 400'd). These options map the
// enum values to human labels; the optional notes feed DisqualificationNotes.
const DISQUALIFICATION_REASONS: ReadonlyArray<{ value: DisqualificationReason; label: string }> = [
  { value: "NoRequiredLicense", label: "No required license" },
  { value: "WrongDealType", label: "Wrong deal type" },
  { value: "GeographicMismatch", label: "Geographic mismatch" },
  { value: "RegulatoryIssue", label: "Regulatory issue" },
  { value: "Unresponsive", label: "Unresponsive" },
  { value: "NotInterested", label: "Not interested" },
  { value: "CompetitorRelationship", label: "Competitor relationship" },
  { value: "DuplicateRecord", label: "Duplicate record" },
  { value: "Other", label: "Other" },
];

interface DisqualifyLeadModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: DisqualificationReason, notes: string) => void;
  pending?: boolean;
}

export function DisqualifyLeadModal({ open, onClose, onConfirm, pending }: DisqualifyLeadModalProps) {
  const [reason, setReason] = useState<DisqualificationReason | "">("");
  const [notes, setNotes] = useState("");

  // Reset the form each time the modal is opened so a prior attempt doesn't
  // leak into the next lead.
  useEffect(() => {
    if (open) {
      setReason("");
      setNotes("");
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Disqualify lead"
      size="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!reason || pending}
            onClick={() => {
              if (!reason) return;
              onConfirm(reason, notes.trim());
            }}
          >
            <Ban size={14} strokeWidth={1.5} aria-hidden />
            {pending ? "Disqualifying…" : "Disqualify"}
          </Button>
        </>
      }
    >
      <p className="text-small text-onyx mb-space-3">
        Select a reason code for disqualifying this lead. You can add optional
        notes with more detail.
      </p>

      <label className="block">
        <span className="block text-caption uppercase tracking-wide text-gray-mid mb-space-1">
          Reason code <span className="text-status-error">*</span>
        </span>
        <select
          className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
          value={reason}
          onChange={(e) => setReason(e.target.value as DisqualificationReason | "")}
          aria-label="Disqualification reason code"
        >
          <option value="" disabled>
            Select a reason…
          </option>
          {DISQUALIFICATION_REASONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block mt-space-3">
        <span className="block text-caption uppercase tracking-wide text-gray-mid mb-space-1">
          Notes (optional)
        </span>
        <Textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any detail about why this lead was disqualified…"
        />
      </label>
    </Modal>
  );
}
